/**
 * Identify prescreener lists from Mash's segment type.
 */
export const isPrescreenerList = list => list.segmentType?.id === "PRESCREENER_LIST";

/**
 * Get the prescreener choices for one segment list.
 *
 * A prescreener is compatible when one of its child segments has a screensSegmentType
 * that matches the list's segment type. Choices include lists not yet on the brand.
 *
 * @param {Object} list The segment list that needs a prescreener.
 * @param {Object[]} lists Available segment lists from Mash's type=SEGMENT_LIST endpoint.
 * @param {Object[]} segments Child segments from Mash's type=SEGMENT endpoint.
 * @returns {Object[]} Compatible prescreener lists.
 */
export function getCompatiblePrescreeners(list, lists, segments) {
    const children = new Map(segments.map(segment => [segment.id, segment]));
    return lists.filter(candidate => isPrescreenerList(candidate) && candidate.children?.some(id => {
        const child = children.get(id);
        return child?.screensSegmentType && child.screensSegmentType.id === list.segmentType?.id;
    }));
}

/**
 * Build a brand segment update for Mash without changing the supplied brand draft.
 *
 * @param {Object} draft Brand segment selections and prescreener assignments.
 * @param {Object[]} availableLists Fresh segment lists from Mash. Their segment types take
 * precedence over the stored brand segment types when identifying prescreeners.
 * @param {Object} originalMappings Previous prescreener assignments, keyed by segment-list ID.
 * Used to identify removed assignments. Defaults to the draft's assignments.
 * @returns {Object} Brand update with segment-list IDs, active IDs, lists and prescreener assignments.
 */
export function buildBrandSegmentPayload(draft, availableLists, originalMappings = draft.segmentListPrescreenerIds || {}) {
    const lists = new Map([...(draft.segmentLists || []), ...availableLists].map(list => [list.id, list]));
    const mappings = Object.fromEntries(Object.entries(draft.segmentListPrescreenerIds || {})
        .filter(([id]) => Array.from(draft.segmentListIds).includes(Number(id))));
    const prescreenerIds = new Set([...lists.values()].filter(isPrescreenerList).map(list => list.id));
    const managedIds = new Set([...Object.values(originalMappings), ...Object.values(draft.segmentListPrescreenerIds || {})]);
    managedIds.forEach(id => prescreenerIds.add(id));
    // Only remove prescreeners whose mappings were removed, not existing standalone selections.
    const selected = new Set(Array.from(draft.segmentListIds).filter(id => !managedIds.has(id)));
    Object.values(mappings).forEach(id => selected.add(id));
    const active = new Set(Array.from(draft.activeSegmentListIds)
        .filter(id => selected.has(id) && !prescreenerIds.has(id)));
    return {
        id: draft.id,
        name: draft.name,
        segmentListIds: [...selected],
        activeSegmentListIds: [...active],
        segmentListPrescreenerIds: mappings,
        segmentLists: [...selected].filter(id => lists.has(id)).map(id => ({
            ...lists.get(id), selected: true, active: active.has(id)
        }))
    };
}
